import { use, useEffect } from 'react';
import type { Account, Service } from '../../redux/types.ts';
import { saveRecentService } from '../../utils/recentServices.ts';
import { isUrlAbsolute, redirectIfServiceEnable } from '../../utils/urlUtils.ts';
import { useNavigate } from 'react-router';
import { setFrameworkMode } from '../../redux/slices/layoutSlice.ts';
import { deleteAccountToken } from '../../utils/tokenServices.ts';
import { deleteRecentAccountId } from '../../utils/recentAccount.ts';
import { useAppDispatch } from '../../redux/store.ts';
import { useLazyGetAccountTokenQuery } from '../../redux/api/accountApi.ts';
import { useLazyGetServicesQuery } from '../../redux/api/serviceApi.ts';
import MainContextProvider from '../../context/MainContextProvider.tsx';

const HeaderEvents = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { setIsLoading } = use(MainContextProvider);

  const [getAccountToken] = useLazyGetAccountTokenQuery();
  const [getServicesList] = useLazyGetServicesQuery();

  const selectSearchOption = (e: CustomEvent<Service>) => {
    const option = e.detail;

    if (!option.enabled) return;

    saveRecentService(option);

    if (isUrlAbsolute(option.path)) {
      window.open(option.path, '_blank');
      return;
    }

    navigate(option.path);
  };

  const onChangeAccount = (e: CustomEvent<Account>) => {
    const account = e.detail;
    getAccountToken({ accountId: Number(account.accountId) })
      .unwrap()
      .then(() => {
        dispatch(setFrameworkMode('ACCOUNT'));
        getServicesList({ isForAccount: true }).unwrap().then(redirectIfServiceEnable());
      });
  };

  const onRedirectToSelectAccount = () => {
    const { pathname, search } = window.location;
    navigate(`/select-account?redirect=${pathname}${search}`);
  };

  const onRedirectToCreateAccount = () => {
    const { pathname, search } = window.location;
    navigate(`/create-account?redirect=${pathname}${search}`);
  };

  const backToUserMode = () => {
    dispatch(setFrameworkMode('USER'));
    getServicesList({ isForAccount: false });
    deleteAccountToken();
    deleteRecentAccountId();
    navigate('/');
  };

  const openProfile = () => {
    navigate('/users');
  };

  const setLoader = (e: CustomEvent<boolean>) => {
    setIsLoading(e.detail);
  };

  useEffect(() => {
    window.addEventListener('framework-select-SearchOption', selectSearchOption as EventListener);
    window.addEventListener('framework-change-Account', onChangeAccount as EventListener);
    window.addEventListener('framework-redirect-SelectAccount', onRedirectToSelectAccount as EventListener);
    window.addEventListener('framework-redirect-CreateAccount', onRedirectToCreateAccount as EventListener);
    window.addEventListener('framework-back-to-UserMode', backToUserMode as EventListener);
    window.addEventListener('framework-open-profile', openProfile as EventListener);
    window.addEventListener('framework-set-loader', setLoader as EventListener);

    return () => {
      window.removeEventListener('framework-select-SearchOption', selectSearchOption as EventListener);
      window.removeEventListener('framework-change-Account', onChangeAccount as EventListener);
      window.removeEventListener('framework-redirect-SelectAccount', onRedirectToSelectAccount as EventListener);
      window.removeEventListener('framework-redirect-CreateAccount', onRedirectToCreateAccount as EventListener);
      window.removeEventListener('framework-back-to-UserMode', backToUserMode as EventListener);
      window.removeEventListener('framework-open-profile', openProfile as EventListener);
      window.removeEventListener('framework-set-loader', setLoader as EventListener);
    };
  }, []);

  return <></>;
};

export default HeaderEvents;
